import { Box, Divider, Stack, useMediaQuery, useTheme } from '@mui/material';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ICourse } from '../api/CourseApi';
import { DialogApi } from '../api/DialogApi';
import { DialogPrompt, DialogPromptApi } from '../api/DialogPromptApi';
import { ILesson } from '../api/LessonApi';
import { UserDialogPromptApi, UserDialogPromptReviewResponse, UserDialogPromptUserSummary } from '../api/UserDialogPromptApi';
import AppBarContext from '../context/AppBarContext';
import DialogPromptFooterContext from '../context/DialogPromptFooterContext';
import { SentenceScoreDialogProvider } from '../context/SentenceScoreDialogContext';
import { DialogPromptComponentFactory } from './DialogView';
import { handleErrorMessage } from './ErrorDialog';

interface ReviewViewProps {
    userDialogPromptReviewResponse: UserDialogPromptReviewResponse;
    lesson: ILesson;
    reviewCourse: ICourse;
    updateUserDialogPromptSummary: (summaryList: UserDialogPromptUserSummary[]) => void;
}

export function isReviewLesson(reviewCourse: ICourse, lesson: ILesson) {
    if (lesson.course_id == reviewCourse.id) {
        if (lesson.name.toLowerCase() == "today") {
            return true;
        } else if (lesson.name.toLowerCase() == "yesterday") {
            return true;
        } else if (lesson.name.toLowerCase() == "last week") {
            return true;
        } else if (lesson.name.toLowerCase() == "last month") {
            return true;
        } else if (lesson.name.toLowerCase() == "before last month") {
            return true;
        }
    }
    return false;
}

export function isReviewLessonHidden(userDialogPromptReviewResponse: UserDialogPromptReviewResponse, lesson: ILesson, reviewCourse?: ICourse) {
    let is_visible = !lesson.is_hidden
    if (reviewCourse && lesson.course_id == reviewCourse.id) {
        if (lesson.name.toLowerCase() == "today") {
            is_visible = userDialogPromptReviewResponse.today_dialog_prompt_list && userDialogPromptReviewResponse.today_dialog_prompt_list.length > 0
        } else if (lesson.name.toLowerCase() == "yesterday") {
            is_visible = userDialogPromptReviewResponse.yesterday_dialog_prompt_list && userDialogPromptReviewResponse.yesterday_dialog_prompt_list.length > 0
        } else if (lesson.name.toLowerCase() == "last week") {
            is_visible = userDialogPromptReviewResponse.last_week_dialog_prompt_list && userDialogPromptReviewResponse.last_week_dialog_prompt_list.length > 0
        } else if (lesson.name.toLowerCase() == "last month") {
            is_visible = userDialogPromptReviewResponse.last_month_dialog_prompt_list && userDialogPromptReviewResponse.last_month_dialog_prompt_list.length > 0
        } else if (lesson.name.toLowerCase() == "before last month") {
            is_visible = userDialogPromptReviewResponse.before_last_month_dialog_prompt_list && userDialogPromptReviewResponse.before_last_month_dialog_prompt_list.length > 0
        }
    }
    return !is_visible
}


export function getReviewDialogPromptList(userDialogPromptReviewResponse: UserDialogPromptReviewResponse, lesson: ILesson, reviewCourse?: ICourse) {
    if (reviewCourse && lesson.course_id == reviewCourse.id) {
        if (lesson.name.toLowerCase() == "today") {
            return userDialogPromptReviewResponse.today_dialog_prompt_list
        } else if (lesson.name.toLowerCase() == "yesterday") {
            return userDialogPromptReviewResponse.yesterday_dialog_prompt_list
        } else if (lesson.name.toLowerCase() == "last week") {
            return userDialogPromptReviewResponse.last_week_dialog_prompt_list
        } else if (lesson.name.toLowerCase() == "last month") {
            return userDialogPromptReviewResponse.last_month_dialog_prompt_list
        } else if (lesson.name.toLowerCase() == "before last month") {
            return userDialogPromptReviewResponse.before_last_month_dialog_prompt_list
        }
    }
    return []
}

const ReviewView: React.FC<ReviewViewProps> = ({ userDialogPromptReviewResponse, lesson, reviewCourse, updateUserDialogPromptSummary, }) => {

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    const { actionList, clearFooterList } = React.useContext(DialogPromptFooterContext)

    const [dialogPromptList, setDialogPromptList] = React.useState<DialogPrompt[]>([]);

    const chatBubbleContainerRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const reviewDialogPromptList = getReviewDialogPromptList(userDialogPromptReviewResponse, lesson, reviewCourse);

        DialogApi.getAll(lesson.id)
            .then(response => {
                if (response.status < 300) {
                    return response.data;
                }
                throw new Error("Unable to DialogApi.getAll() " + response.status);
            })
            .then(list => {
                const visibleList = list.filter(dialog => !dialog.is_hidden);

                if (visibleList.length === 0) {
                    // No visible dialogs, just use the review list
                    setDialogPromptList(reviewDialogPromptList);
                    return;
                }

                // Only call getAll if there is at least one dialog
                DialogPromptApi.getAll(visibleList[0].id)
                    .then(response => {
                        if (response.status < 300) {
                            return response.data;
                        }
                        throw new Error("Unable to DialogPromptApi.getAll() " + response.status);
                    })
                    .then(list => {
                        const visibleDialogPrompts = list.filter(dp => !dp.is_hidden);
                        const newList = [...reviewDialogPromptList];

                        if (visibleDialogPrompts.length > 0) {
                            newList.push(visibleDialogPrompts[0]);
                        }

                        setDialogPromptList(newList);
                    })
                    .catch(err => {
                        handleErrorMessage(err, setErrorMessage, history, location);
                    });
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            });

        // Initialize with the review list even if fetch is pending
        setDialogPromptList(reviewDialogPromptList);

    }, [userDialogPromptReviewResponse, lesson, reviewCourse, history, location, setErrorMessage]);

    const [currentDialogPromptIndex, setCurrentDialogPromptIndex] = React.useState(1);
    const activeDialogPromptId = dialogPromptList[currentDialogPromptIndex - 1]?.id ?? null;

    React.useEffect(() => {
        if (chatBubbleContainerRef.current) {
            chatBubbleContainerRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [currentDialogPromptIndex]);

    React.useLayoutEffect(() => {
        clearFooterList();
    }, [activeDialogPromptId, clearFooterList]);

    const setDialogPromptComplete = (dialogPromptId: number) => {

        // check the dialogPromptId, old dialogs can be clicked on.
        const currentDialogPrompt = dialogPromptList[currentDialogPromptIndex - 1];
        if (currentDialogPrompt && currentDialogPrompt.id == dialogPromptId) {
            clearFooterList();

            // dialogPrormptId == -1 if "lets continue" prompt, so don't complete it
            if (dialogPromptId >= 0) {
                UserDialogPromptApi.completeDialogPrompt(dialogPromptId, userDialogPromptReviewResponse)
                    .then(response => {
                        if (response.status < 300) {
                            return response.data;
                        }
                        throw new Error("Unable to UserDialogPromptApi.completeDialogPrompt " + response.status)

                    })
                    .then(summary => {
                        updateUserDialogPromptSummary(summary)
                    })
                    .catch(err => {
                        handleErrorMessage(err, setErrorMessage, history, location);
                    })

            }

            // currentDialogPromptIndex should always be right ... but search by ID anyway
            const slicedDialogPromptList = dialogPromptList.slice(currentDialogPromptIndex - 1);

            // Use findIndex() on the sliced array to find the next index of the dialogPromptId
            const indexOfTargetId = slicedDialogPromptList.findIndex((dialogPrompt) => dialogPrompt.id === dialogPromptId);

            // If the indexOfTargetId is not -1 (i.e., the targetId is found), adjust the index based on the currentDialogPromptIndex
            const finalIndex = indexOfTargetId !== -1 ? indexOfTargetId + currentDialogPromptIndex - 1 : -1;
            if (finalIndex >= 0) {
                // +1 because 1 based, +1 to advance to next dialog prompt
                setCurrentDialogPromptIndex(finalIndex + 2);
            } else {
                // if not found, just goto next index
                setCurrentDialogPromptIndex((prevIndex) => (prevIndex + 1));
            }
        }

    }

    const selectTeacherIndex = dialogPromptList.findIndex(
        (dialogPrompt) => dialogPrompt.class_name == "DialogPromptSelectTeacherModel"
    );

    // icon size
    // "small"     20px    for compact UI
    // "medium"    24px    standard
    // "large"     36px    prominent icons
    const theme = useTheme();
    const isCompact = useMediaQuery(theme.breakpoints.down("sm"));

    const actionButtonSize = isCompact ? '24px' : '36px'

    return (
        <Stack sx={{ margin: '0 auto', width: '95%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <SentenceScoreDialogProvider>
                <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                    {dialogPromptList.slice(0, currentDialogPromptIndex).map((dialogPrompt, index) => {
                        const showAvatar = index > selectTeacherIndex;
                        return DialogPromptComponentFactory(
                            dialogPrompt,
                            setDialogPromptComplete,
                            chatBubbleContainerRef,
                            showAvatar,
                            true,
                            index === currentDialogPromptIndex - 1,
                        )
                    })}
                    <div ref={chatBubbleContainerRef} />
                </div>
            </SentenceScoreDialogProvider>
            {/* FOOTER */}
            <Stack direction="column" alignItems="center" justifyContent="center" sx={{ flexShrink: 0 }}>
                <Divider sx={{ width: '90%', borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }} />
                {/* ACTION buttons */}
                <Stack direction="row" alignItems="center" justifyContent="center" flexWrap="wrap" rowGap={1} sx={{ flexGrow: 1, my: 1, width: '100%', minHeight: 60 }} gap={1}>
                    {actionList.map((action, index) => (
                        <Box key={index} sx={{ minHeight: actionButtonSize, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{action}</Box>
                    ))}
                </Stack>
            </Stack>
        </Stack >
    );
};

export default ReviewView;
